import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MapPin, Clock, Briefcase, Search, Star, Code, Users, TrendingUp, Building2, Wrench, ShoppingBag, Phone as PhoneIcon, Heart, Package, Factory, Truck, GraduationCap } from 'lucide-react';
import { getFeaturedJobs, getCategories } from '../services/api';
import LocationAutocomplete from '../components/LocationAutocomplete';

const iconMap = {
  Code, Users, TrendingUp, Building2, Wrench, ShoppingBag, Phone: PhoneIcon, Heart, Package, Factory, Truck, GraduationCap, Briefcase
};

export default function Home() {
  const [jobs, setJobs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [location, setLocation] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    getFeaturedJobs().then(res => setJobs(res.data)).catch(() => {});
    getCategories().then(res => setCategories(res.data)).catch(() => {});
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    navigate(`/jobs?search=${search}&location=${location}`);
  };

  return (
    <>
      <section className="hero">
        <div className="hero-content">
          <h1>Find Your <span>Dream Career</span><br />Starts Here</h1>
          <p>Connect with top employers and discover opportunities that match your skills. Your next career move is just a search away.</p>
          <form className="hero-search" onSubmit={handleSearch}>
            <input type="text" placeholder="Job title, skills, or company..." value={search} onChange={(e) => setSearch(e.target.value)} />
            <LocationAutocomplete
              value={location}
              onChange={setLocation}
              placeholder="Location..."
            />
            <button type="submit"><Search size={18} /> Search</button>
          </form>
          <div className="hero-stats">
            <div className="stat"><div className="stat-number">500+</div><div className="stat-label">Companies</div></div>
            <div className="stat"><div className="stat-number">10K+</div><div className="stat-label">Job Seekers</div></div>
            <div className="stat"><div className="stat-number">2K+</div><div className="stat-label">Placements</div></div>
          </div>
        </div>
      </section>

      {jobs.length > 0 && (
        <section className="section section-alt">
          <div className="container">
            <div className="section-header">
              <div className="badge">Job Spotlight</div>
              <h2>Featured Job Opportunities</h2>
              <p>Handpicked openings from top employers across India</p>
            </div>
            <div className="jobs-grid">
              {jobs.map(job => (
                <div className="job-card" key={job.id}>
                  <div className="job-card-header">
                    <div>
                      <h3>{job.title} {job.is_featured === 1 && <Star size={16} color="#f59e0b" fill="#f59e0b" style={{ verticalAlign: 'middle' }} />}</h3>
                      <div className="company">{job.employer_name || job.company}</div>
                    </div>
                    <span className="job-type-badge">{job.type}</span>
                  </div>
                  <div className="job-meta">
                    <div className="job-meta-item"><MapPin size={16} /> {job.location}</div>
                    <div className="job-meta-item"><Clock size={16} /> {job.experience}</div>
                    <div className="job-meta-item"><Briefcase size={16} /> {job.category}</div>
                  </div>
                  <div className="job-card-footer">
                    <span className="job-salary">{job.salary}</span>
                    <Link to={`/jobs/${job.id}`} className="btn-apply">View & Apply</Link>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ textAlign: 'center', marginTop: 40 }}>
              <Link to="/jobs" className="btn-apply" style={{ padding: '14px 40px', fontSize: '1rem' }}>View All Openings</Link>
            </div>
          </div>
        </section>
      )}

      <section className="section section-alt" style={{ background: 'white' }}>
        <div className="container">
          <div className="section-header">
            <div className="badge">Our Services</div>
            <h2>Services We Offer</h2>
            <p>Comprehensive recruitment solutions for job seekers and employers</p>
          </div>
          <div className="steps-grid">
            <Link to="/services/placement" className="value-card" style={{ textDecoration: 'none' }}>
              <div className="value-icon"><Briefcase size={24} /></div>
              <h3>Placement Consultant</h3>
              <p>Job seekers searching for suitable opportunities can connect with us for personalized placement consulting.</p>
            </Link>
            <Link to="/services/hr" className="value-card" style={{ textDecoration: 'none' }}>
              <div className="value-icon"><Users size={24} /></div>
              <h3>HR Consultant</h3>
              <p>Comprehensive HR consulting services to help organizations build strong teams and improve retention.</p>
            </Link>
            <Link to="/services/manpower" className="value-card" style={{ textDecoration: 'none' }}>
              <div className="value-icon"><TrendingUp size={24} /></div>
              <h3>Manpower Recruitment</h3>
              <p>End-to-end manpower recruitment solutions across industries for permanent and contract positions.</p>
            </Link>
          </div>
        </div>
      </section>

      {categories.length > 0 && (
        <section className="section">
          <div className="container">
            <div className="section-header">
              <div className="badge">Industries We Serve</div>
              <h2>Browse by Category</h2>
              <p>Find opportunities in your preferred industry</p>
            </div>
            <div className="categories-grid">
              {categories.map((cat) => {
                const Icon = iconMap[cat.icon] || Briefcase;
                return (
                  <Link to={`/jobs?category=${cat.name}`} className="category-card" key={cat.id}>
                    <div className="category-icon"><Icon size={24} /></div>
                    <h3>{cat.name}</h3>
                    {cat.description && <span>{cat.description}</span>}
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <section className="section section-dark">
        <div className="container">
          <div className="section-header">
            <div className="badge">How It Works</div>
            <h2>Your Path to Success</h2>
            <p>Three simple steps to land your dream job</p>
          </div>
          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">1</div>
              <h3>Search Jobs</h3>
              <p>Browse through thousands of job listings from top companies across various industries.</p>
            </div>
            <div className="step-card">
              <div className="step-number">2</div>
              <h3>Apply Online</h3>
              <p>Submit your application with your resume and cover letter directly through our portal.</p>
            </div>
            <div className="step-card">
              <div className="step-number">3</div>
              <h3>Get Hired</h3>
              <p>Connect with employers, attend interviews, and start your new career journey.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <h2>Ready to Take the Next Step?</h2>
        <p>Join thousands of job seekers who found their dream job through CareerQuess</p>
        <div className="cta-buttons">
          <Link to="/jobs" className="btn-cta btn-cta-primary">Browse Jobs</Link>
          <Link to="/post-resume" className="btn-cta btn-cta-outline">Post Your Resume</Link>
        </div>
      </section>
    </>
  );
}
